import { Navigate } from 'react-router-dom';
import { LockKey, ShieldCheck, SignIn as SignInIcon, Sparkle, type Icon } from '@phosphor-icons/react';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui';

// The school's programs, shown on the public landing. Static copy (this page is
// pre-auth, so it can't call the courses API) — add a line here for a new program.
const PROGRAMS: { icon: Icon; title: string; blurb: string }[] = [
  {
    icon: ShieldCheck,
    title: 'Azure Cloud Security',
    blurb: '26 weeks · hands-on labs, SOC tooling, real projects, and AI mock interviews.',
  },
  {
    icon: Sparkle,
    title: 'Build and Secure Your First AI-Assisted Website',
    blurb: 'Beginner-friendly · plan, build, publish, and secure a real website with Claude.',
  },
];

export function SignIn() {
  const { status, login } = useAuth();
  if (status === 'authenticated') return <Navigate to="/" replace />;

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Brand panel */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-brand-navy p-12 text-white lg:flex">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="" className="h-12 w-12 shrink-0" />
          <div className="leading-tight">
            <div className="font-display text-xl font-extrabold tracking-wide text-brand-teal-bright">
              CYBER<span className="text-white">VAULT</span>
            </div>
            <div className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">School</div>
          </div>
        </div>
        <div>
          <div className="label-mono text-brand-teal-bright">Members only</div>
          <h1 className="mt-3 max-w-md font-display text-4xl font-extrabold leading-tight">
            Get job-ready in cloud security and AI.
          </h1>
          <p className="mt-4 max-w-md text-slate-300">
            Paid, mentor-led programs. Lessons unlock as they're recorded, and your instructor grants access once
            your enrollment is confirmed.
          </p>
          <ul className="mt-8 space-y-4">
            {PROGRAMS.map((p) => (
              <li key={p.title} className="flex items-start gap-3">
                <p.icon size={22} weight="fill" className="mt-0.5 shrink-0 text-brand-teal-bright" />
                <div>
                  <div className="font-semibold text-white">{p.title}</div>
                  <div className="text-sm text-slate-300">{p.blurb}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <p className="text-sm text-slate-400">cybervaultschool.com</p>
      </div>

      {/* Sign-in card */}
      <div className="flex items-center justify-center bg-brand-light p-6">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <ShieldCheck size={28} weight="fill" className="text-brand-teal" />
            <span className="font-display text-xl font-extrabold text-brand-navy">CyberVault School</span>
          </div>
          <h2 className="text-2xl font-bold">Sign in</h2>
          <p className="mt-2 text-sm text-slate-500">
            Use Google, Microsoft, or your email to access the members area.
          </p>

          <Button
            variant="primary"
            className="mt-6 w-full"
            icon={SignInIcon}
            loading={status === 'loading'}
            onClick={() => login()}
          >
            Sign in or create your account
          </Button>

          <button
            type="button"
            onClick={() => login()}
            className="mt-3 block w-full text-center text-sm font-semibold text-brand-teal transition hover:underline"
          >
            Forgot your password?
          </button>
          <p className="mt-1 text-center text-xs text-slate-400">
            Continue to the sign-in page, then choose <span className="font-semibold">“Forgot password”</span> to reset it
            by email. If you signed up with a social account, reset it with that provider instead.
          </p>

          <div className="mt-6 flex items-start gap-2 rounded-xl bg-white p-4 text-xs text-slate-500 shadow-card">
            <LockKey size={18} className="mt-0.5 shrink-0 text-brand-teal" />
            <p>
              Authentication is handled by Microsoft Entra External ID. After creating your account, your instructor
              confirms your enrollment before course access is available.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
